(() => {
  const SECTION_SEL = '[data-pcs]';

  /**
   * Split spec textarea into items.
   * - Normal: one line = one item
   * - Quoted: "a\nb\nc" = one item, inner newlines kept for display
   */
  function parseSpecItems(raw) {
    if (raw == null) return [];
    const s = String(raw).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    if (!s.trim()) return [];

    const items = [];
    let current = '';
    let inQuotes = false;
    let i = 0;

    while (i < s.length) {
      const ch = s[i];

      if (inQuotes) {
        if (ch === '"') {
          if (s[i + 1] === '"') {
            current += '"';
            i += 2;
            continue;
          }
          inQuotes = false;
          i += 1;
          continue;
        }
        current += ch;
        i += 1;
        continue;
      }

      if (ch === '"') {
        inQuotes = true;
        i += 1;
        continue;
      }

      if (ch === '\n') {
        items.push(current);
        current = '';
        i += 1;
        continue;
      }

      current += ch;
      i += 1;
    }

    items.push(current);

    // Drop leading/trailing empty entries from outer blank lines only
    while (items.length && items[0].trim() === '') items.shift();
    while (items.length && items[items.length - 1].trim() === '') items.pop();

    return items.map((item) => item.replace(/^\n+|\n+$/g, ''));
  }

  function normalizeValues(raw, labelCount) {
    const parsed = parseSpecItems(raw);
    const values = [];
    for (let i = 0; i < labelCount; i += 1) {
      const val = parsed[i] == null ? '' : String(parsed[i]).trim();
      values.push(val === '' ? '/' : parsed[i]);
    }
    return values;
  }

  function normalizeGroups(data) {
    if (!data || !Array.isArray(data.groups)) return data;

    data.groups = data.groups.map((group) => {
      const labels = parseSpecItems(group.labelsRaw).map((l) => l.trim()).filter((l) => l !== '');
      const main = group.main
        ? {
            ...group.main,
            values: normalizeValues(group.main.valuesRaw, labels.length),
          }
        : null;
      const products = (group.products || []).map((p) => ({
        ...p,
        values: normalizeValues(p.valuesRaw, labels.length),
      }));

      return {
        ...group,
        labels,
        main,
        products,
      };
    });

    return data;
  }

  class ProductCompareSelect {
    constructor(root) {
      this.root = root;
      this.data = normalizeGroups(this.parseData());
      if (!this.data || !this.data.groups || !this.data.groups.length) return;

      this.initialRows = Number(root.dataset.pcsInitialRows || 4);
      this.expanded = root.classList.contains('is-expanded');
      this.viewAllBtn = root.querySelector('[data-pcs-view-all]');
      this.specsEl = root.querySelector('[data-pcs-specs]');
      this.footerEl = root.querySelector('[data-pcs-footer]');

      this.slot0 = root.querySelector('[data-pcs-slot="0"]');
      this.slot1 = root.querySelector('[data-pcs-slot="1"]');
      this.slot2 = root.querySelector('[data-pcs-slot="2"]');

      this.currentGroupId = this.data.groups[0].id;
      this.activeKeys = {
        0: null,
        1: null,
        2: null,
      };

      this.bindDropdownChrome();
      this.initGroupDropdown();
      this.bindViewAll();
      this.applyGroup(this.currentGroupId, { resetProducts: true });
    }

    parseData() {
      const el = this.root.querySelector('[data-pcs-json]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    getGroup(groupId) {
      return this.data.groups.find((g) => g.id === groupId);
    }

    getItem(group, key) {
      if (!group) return null;
      if (group.main && group.main.key === key) return group.main;
      return (group.products || []).find((p) => p.key === key) || null;
    }

    getDropdown(slot) {
      return slot ? slot.querySelector('[data-pcs-dropdown]') : null;
    }

    bindDropdownChrome() {
      if (this.root.dataset.pcsDropdownBound) return;
      this.root.dataset.pcsDropdownBound = '1';

      this.root.querySelectorAll('[data-pcs-dropdown]').forEach((dropdown) => {
        const trigger = dropdown.querySelector('[data-pcs-trigger]');
        if (!trigger) return;

        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (dropdown.classList.contains('is-disabled')) return;
          const willOpen = !dropdown.classList.contains('is-open');
          this.closeAllDropdowns();
          if (willOpen) this.openDropdown(dropdown);
        });
      });

      this._onDocClick = (e) => {
        if (!this.root.isConnected) return;
        if (!this.root.contains(e.target) || !e.target.closest('[data-pcs-dropdown]')) {
          this.closeAllDropdowns();
        }
      };

      this._onDocKey = (e) => {
        if (e.key === 'Escape') this.closeAllDropdowns();
      };

      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onDocKey);
    }

    openDropdown(dropdown) {
      dropdown.classList.add('is-open');
      const trigger = dropdown.querySelector('[data-pcs-trigger]');
      const menu = dropdown.querySelector('[data-pcs-menu]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      if (menu) menu.hidden = false;
    }

    closeDropdown(dropdown) {
      dropdown.classList.remove('is-open');
      const trigger = dropdown.querySelector('[data-pcs-trigger]');
      const menu = dropdown.querySelector('[data-pcs-menu]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (menu) menu.hidden = true;
    }

    closeAllDropdowns() {
      this.root.querySelectorAll('[data-pcs-dropdown].is-open').forEach((el) => {
        this.closeDropdown(el);
      });
    }

    setDropdownOptions(dropdown, options, selectedValue, { disabled = false } = {}) {
      if (!dropdown) return;

      const menu = dropdown.querySelector('[data-pcs-menu]');
      const labelEl = dropdown.querySelector('[data-pcs-trigger-label]');
      const trigger = dropdown.querySelector('[data-pcs-trigger]');
      if (!menu || !labelEl) return;

      menu.innerHTML = '';
      let selectedLabel = '';

      options.forEach((opt) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pcs__select-option';
        btn.dataset.value = opt.value;
        btn.dataset.pcsOption = '';
        btn.setAttribute('role', 'option');
        btn.textContent = opt.label;
        if (opt.value === selectedValue) {
          btn.classList.add('is-selected');
          btn.setAttribute('aria-selected', 'true');
          selectedLabel = opt.label;
        }
        if (opt.disabled) {
          btn.classList.add('is-disabled');
          btn.disabled = true;
        }
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (btn.classList.contains('is-disabled')) return;
          this.handleDropdownPick(dropdown, opt.value, opt.label);
        });
        li.appendChild(btn);
        menu.appendChild(li);
      });

      labelEl.textContent = selectedLabel || (options[0] && options[0].label) || '';
      dropdown.dataset.value = selectedValue || (options[0] && options[0].value) || '';
      dropdown.classList.toggle('is-disabled', disabled || options.length === 0);
      if (trigger) trigger.disabled = disabled || options.length === 0;
    }

    handleDropdownPick(dropdown, value, label) {
      const labelEl = dropdown.querySelector('[data-pcs-trigger-label]');
      if (labelEl) labelEl.textContent = label;
      dropdown.dataset.value = value;

      dropdown.querySelectorAll('[data-pcs-option]').forEach((btn) => {
        const selected = btn.dataset.value === value;
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-selected', selected ? 'true' : 'false');
      });

      this.closeAllDropdowns();

      if (dropdown.hasAttribute('data-pcs-dropdown-group')) {
        this.applyGroup(value, { resetProducts: true });
        return;
      }

      const slot = dropdown.closest('[data-pcs-slot]');
      if (!slot) return;
      const group = this.getGroup(this.currentGroupId);
      const item = this.getItem(group, value);
      if (!item) return;
      this.renderSlot(slot, item);
      this.activeKeys[slot.dataset.pcsSlot] = item.key;
      this.syncProductOptionDisabled();
      this.renderSpecs(group);
    }

    initGroupDropdown() {
      const dropdown = this.getDropdown(this.slot0);
      if (!dropdown) return;

      const options = this.data.groups.map((g) => ({
        value: g.id,
        label: (g.main && (g.main.optionLabel || g.main.title)) || g.id,
      }));

      this.setDropdownOptions(dropdown, options, this.currentGroupId);
    }

    bindViewAll() {
      if (!this.viewAllBtn) return;

      this.viewAllBtn.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.root.classList.toggle('is-expanded', this.expanded);
        this.viewAllBtn.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');

        const expandedLabel = this.viewAllBtn.dataset.labelExpanded || 'Show Less';
        const collapsedLabel = this.viewAllBtn.dataset.labelCollapsed || 'View All';
        const labelEl = this.viewAllBtn.querySelector('[data-pcs-view-all-label]');
        if (labelEl) {
          labelEl.textContent = this.expanded ? expandedLabel : collapsedLabel;
        }

        this.applySpecVisibility();
      });
    }

    applyGroup(groupId, { resetProducts } = {}) {
      const group = this.getGroup(groupId);
      if (!group) return;

      this.currentGroupId = group.id;

      const groupDropdown = this.getDropdown(this.slot0);
      if (groupDropdown) {
        const label =
          (group.main && (group.main.optionLabel || group.main.title)) || group.id;
        this.setDropdownOptions(
          groupDropdown,
          this.data.groups.map((g) => ({
            value: g.id,
            label: (g.main && (g.main.optionLabel || g.main.title)) || g.id,
          })),
          group.id
        );
        const labelEl = groupDropdown.querySelector('[data-pcs-trigger-label]');
        if (labelEl) labelEl.textContent = label;
      }

      this.renderSlot(this.slot0, group.main);
      this.activeKeys[0] = group.main ? group.main.key : null;

      const products = group.products || [];
      const pick0 = products[0] || null;
      const pick1 = products[1] || products[0] || null;

      this.updateProductSelect(
        this.slot1,
        products,
        resetProducts ? pick0 : this.resolveProduct(products, this.activeKeys[1], pick0)
      );
      this.updateProductSelect(
        this.slot2,
        products,
        resetProducts ? pick1 : this.resolveProduct(products, this.activeKeys[2], pick1)
      );

      this.updateColumnVisibility(products.length);
      this.syncProductOptionDisabled();
      this.renderSpecs(group);
    }

    resolveProduct(products, key, fallback) {
      if (!products.length) return null;
      const found = products.find((p) => p.key === key);
      return found || fallback || products[0];
    }

    updateProductSelect(slot, products, selectedItem) {
      if (!slot) return;
      const dropdown = this.getDropdown(slot);
      if (!dropdown) return;

      const options = products.map((item) => ({
        value: item.key,
        label: item.optionLabel || item.title || item.key,
      }));

      const selectedValue = selectedItem ? selectedItem.key : '';
      this.setDropdownOptions(dropdown, options, selectedValue, {
        disabled: products.length === 0,
      });

      if (selectedItem) {
        this.renderSlot(slot, selectedItem);
        this.activeKeys[slot.dataset.pcsSlot] = selectedItem.key;
      } else {
        this.activeKeys[slot.dataset.pcsSlot] = null;
        this.clearSlot(slot);
      }
    }

    updateColumnVisibility(productCount) {
      const cols = Math.min(3, Math.max(1, 1 + productCount));
      this.root.style.setProperty('--pcs-cols-pc', String(cols));
      this.root.style.setProperty('--pcs-cols-mb', String(cols));

      if (this.slot1) {
        const hide = productCount < 1;
        this.slot1.hidden = hide;
        this.slot1.classList.toggle('pcs__col--hidden', hide);
      }
      if (this.slot2) {
        const hide = productCount < 2;
        this.slot2.hidden = hide;
        this.slot2.classList.toggle('pcs__col--hidden', hide);
      }
    }

    syncProductOptionDisabled() {
      const selected = [this.activeKeys[1], this.activeKeys[2]].filter(Boolean);

      [this.slot1, this.slot2].forEach((slot) => {
        if (!slot || slot.hidden) return;
        const dropdown = this.getDropdown(slot);
        if (!dropdown) return;
        const current = this.activeKeys[slot.dataset.pcsSlot];
        dropdown.querySelectorAll('[data-pcs-option]').forEach((btn) => {
          const disabled = selected.includes(btn.dataset.value) && btn.dataset.value !== current;
          btn.classList.toggle('is-disabled', disabled);
          btn.disabled = disabled;
        });
      });
    }

    clearSlot(slot) {
      if (!slot) return;
      const img = slot.querySelector('[data-pcs-image]');
      if (img) {
        img.removeAttribute('src');
        img.hidden = true;
      }
      const title = slot.querySelector('[data-pcs-title]');
      if (title) title.innerHTML = '';
      const price = slot.querySelector('[data-pcs-price]');
      if (price) price.innerHTML = '';
      const buyNow = slot.querySelector('[data-pcs-buy-now]');
      if (buyNow) {
        buyNow.hidden = true;
        buyNow.setAttribute('data-variant-id', '');
        buyNow.classList.remove('is-sold-out');
      }
      const learnMore = slot.querySelector('[data-pcs-learn-more]');
      if (learnMore) {
        learnMore.hidden = true;
        learnMore.removeAttribute('href');
      }
    }

    renderSlot(slot, item) {
      if (!slot || !item) return;

      slot.dataset.itemKey = item.key;

      const img = slot.querySelector('[data-pcs-image]');
      if (img) {
        if (item.image) {
          img.src = item.image;
          img.alt = item.imageAlt || item.optionLabel || '';
          img.hidden = false;
        } else {
          img.removeAttribute('src');
          img.hidden = true;
        }
      }

      const title = slot.querySelector('[data-pcs-title]');
      if (title) title.innerHTML = item.title || item.optionLabel || '';

      const price = slot.querySelector('[data-pcs-price]');
      if (price) price.innerHTML = item.priceHtml || '';

      const buyNow = slot.querySelector('[data-pcs-buy-now]');
      if (buyNow) {
        if (item.showBuyNow && item.variantId) {
          buyNow.hidden = false;
          buyNow.setAttribute('data-variant-id', String(item.variantId));
          buyNow.textContent = item.available
            ? item.buyNowText || 'Buy Now'
            : item.soldOutText || 'Sold Out';
          buyNow.classList.toggle('is-sold-out', !item.available);
          if (item.available) {
            buyNow.removeAttribute('aria-disabled');
          } else {
            buyNow.setAttribute('aria-disabled', 'true');
          }
        } else {
          buyNow.hidden = true;
          buyNow.setAttribute('data-variant-id', '');
        }
      }

      const learnMore = slot.querySelector('[data-pcs-learn-more]');
      if (learnMore) {
        if (item.showLearnMore && item.learnMoreUrl) {
          learnMore.hidden = false;
          learnMore.href = item.learnMoreUrl;
          learnMore.innerHTML = item.learnMoreText || 'Learn More';
        } else {
          learnMore.hidden = true;
          learnMore.removeAttribute('href');
        }
      }
    }

    renderSpecs(group) {
      if (!this.specsEl) return;

      const labels = group.labels || [];
      const items = [
        group.main,
        this.getItem(group, this.activeKeys[1]),
        this.getItem(group, this.activeKeys[2]),
      ];

      const productCount = (group.products || []).length;
      let html = '';

      labels.forEach((label, rowIndex) => {
        html += `<div class="pcs__spec-row" data-pcs-spec-row>`;
        for (let slot = 0; slot < 3; slot += 1) {
          const hide =
            (slot === 1 && productCount < 1) ||
            (slot === 2 && productCount < 2);
          const raw = items[slot] && items[slot].values ? items[slot].values[rowIndex] : '';
          const value = raw && String(raw).trim() !== '' ? String(raw) : '/';
          const labelHtml = label
            ? `<div class="pcs__spec-label">${this.escapeHtml(label)}</div>`
            : '';
          const hiddenClass = hide ? ' pcs__col--hidden' : '';
          html += `<div class="pcs__spec-cell${hiddenClass}" data-pcs-spec-cell data-slot="${slot}" ${
            hide ? 'hidden' : ''
          }>
            <div class="pcs__spec-value" data-pcs-spec-value>${this.escapeHtml(value)}</div>
            ${labelHtml}
          </div>`;
        }
        html += `</div>`;
      });

      this.specsEl.innerHTML = html;
      this.specsEl.hidden = labels.length === 0;

      if (this.footerEl) {
        this.footerEl.hidden = labels.length <= this.initialRows;
      }

      if (labels.length <= this.initialRows) {
        this.expanded = false;
        this.root.classList.remove('is-expanded');
        if (this.viewAllBtn) {
          this.viewAllBtn.setAttribute('aria-expanded', 'false');
          const labelEl = this.viewAllBtn.querySelector('[data-pcs-view-all-label]');
          if (labelEl) {
            labelEl.textContent = this.viewAllBtn.dataset.labelCollapsed || 'View All';
          }
        }
      }

      this.applySpecVisibility();
    }

    applySpecVisibility() {
      const rows = this.root.querySelectorAll('[data-pcs-spec-row]');
      rows.forEach((row, i) => {
        row.hidden = !this.expanded && i >= this.initialRows;
      });

      if (this.viewAllBtn && this.footerEl && !this.footerEl.hidden) {
        this.viewAllBtn.hidden = rows.length <= this.initialRows;
      }
    }

    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SECTION_SEL).forEach((el) => {
      if (el.dataset.pcsReady) return;
      el.dataset.pcsReady = '1';
      new ProductCompareSelect(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    if (root && root.querySelectorAll) {
      root.querySelectorAll(SECTION_SEL).forEach((el) => {
        delete el.dataset.pcsReady;
        delete el.dataset.pcsDropdownBound;
      });
      if (root.matches && root.matches(SECTION_SEL)) {
        delete root.dataset.pcsReady;
        delete root.dataset.pcsDropdownBound;
      }
    }
    initAll(event.target);
  });
})();
